import { collection, doc, getDocs, onSnapshot, orderBy, query, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getBlob, ref, uploadBytes } from 'firebase/storage';
import { db, functions, storage } from '@/lib/firebase';
import { tratarErro, tratarErroDeCallable } from '@/lib/erros';

/**
 * ★★★ WT lote 1 — TODA CALLABLE PASSA POR AQUI.
 *
 * ⚠⚠ Erro de callable chega ao cliente com o `code` do servidor (`permission-denied`,
 * `failed-precondition`, `resource-exhausted`) — e é **o diagnóstico mais valioso que
 * existe**: ele diz que a tela pediu algo que o servidor recusou. ★ Até agora ele só
 * aparecia na mensagem que a pessoa lia e esquecia.
 *
 * ★ O embrulho **registra e relança**: quem chama continua tratando o erro como antes
 * (as telas leem `e.code` para escolher a mensagem). ⚠ Engolir aqui quebraria todas elas.
 */
const chamarCallable = (nome) => async (dados) => {
  try {
    return await httpsCallable(functions, nome)(dados);
  } catch (erro) {
    throw tratarErroDeCallable(nome, erro);
  }
};


/**
 * Importação do "Contas a receber" do Careflow (VA-2).
 *
 * ★ O NAVEGADOR NÃO LÊ A PLANILHA. Ele só sobe o arquivo e chama as duas
 * callables; quem parseia é o servidor.
 *
 * É o oposto da importação da AGENDA (`recepcao/agendaPdf.js`), que é lida
 * inteiramente aqui — e de propósito, porque lá o arquivo vira mensagem enviada
 * na hora e guardá-lo seria exposição sem contrapartida. Aqui o número vira
 * COMISSÃO de quem importou: ele tem de vir do arquivo, e o arquivo tem de
 * poder ser reconferido depois. Mesma preocupação, respostas diferentes.
 *
 * Se este módulo um dia passar a parsear no cliente, a garantia acaba — sem
 * nenhum teste ficar vermelho. Por isso está escrito aqui.
 */

const colecao = (clinicaId) => collection(db, 'clinicas', clinicaId, 'importacoes');

/** Id da importação: um por tentativa, para cada arquivo ter pasta própria. */
export const novoIdImportacao = (clinicaId) => doc(colecao(clinicaId)).id;

const NOME_ARQUIVO = 'original.xlsx';

/**
 * Sobe o arquivo e devolve o nome com que ele ficou.
 *
 * O nome é FIXO, e não o do arquivo escolhido: o do usuário viria com espaço,
 * acento e barra, e o servidor teria de tratar cada caso para montar o caminho
 * do bucket. Nome fixo remove a categoria inteira de problema — o nome original
 * fica guardado no documento, para o histórico mostrar.
 */
export async function subirPlanilha(clinicaId, importacaoId, arquivo) {
  const caminho = `clinicas/${clinicaId}/importacoes/${importacaoId}/${NOME_ARQUIVO}`;
  await uploadBytes(ref(storage, caminho), arquivo, {
    contentType:
      arquivo.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  return NOME_ARQUIVO;
}

export async function previewImportacao(clinicaId, importacaoId, nomeArquivo) {
  const chamar = chamarCallable('previewImportacaoRecebimentos');
  const { data } = await chamar({ clinicaId, importacaoId, nomeArquivo });
  return data;
}

/**
 * Confirma a importação.
 *
 * `decisoes` é uma lista de `{ idExterno, leadId }` — e SÓ isso. Valor, data e
 * forma não viajam daqui: o servidor relê o arquivo e os deriva de novo. É o que
 * garante que quem importa, e ganha comissão sobre o resultado, não consegue
 * escolher o número.
 */
/**
 * ★★ Travas do fechamento (item 1): `importarMesFechado` é a confirmação EXPLÍCITA de importar
 * parcela de mês já fechado. Sem ela o servidor pula essas linhas, mesmo que venham em `decisoes`.
 */
export async function confirmarImportacao(clinicaId, importacaoId, nomeArquivo, decisoes, { importarMesFechado = false } = {}) {
  const chamar = chamarCallable('confirmarImportacaoRecebimentos');
  const { data } = await chamar({ clinicaId, importacaoId, nomeArquivo, decisoes, importarMesFechado: importarMesFechado === true });
  return data;
}

/** Histórico, do mais recente para o mais antigo. */
/** Leitura ÚNICA do histórico — para o relatório geral achar a última confirmada. */
export async function buscarImportacoes(clinicaId) {
  const snap = await getDocs(query(colecao(clinicaId), orderBy('criadoEm', 'desc'), limit(50)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export function ouvirImportacoes(clinicaId, aoReceber, aoFalhar) {
  const q = query(colecao(clinicaId), orderBy('criadoEm', 'desc'), limit(50));
  return onSnapshot(
    q,
    (snap) => aoReceber(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    tratarErro('as importações', aoFalhar),
  );
}

/** Baixa o arquivo original de uma importação — a conferência do gestor. */
export async function baixarOriginal(importacao) {
  const blob = await getBlob(ref(storage, importacao.arquivoCaminho));
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = importacao.arquivoNome || 'contas-a-receber.xlsx';
  link.click();
  URL.revokeObjectURL(url);
}
